<!--
  SaImpersonationBanner — Day 8 Item A1.

  Surfaces a friendly explanation when a SuperAdmin is using the canonical
  Product Editor without having picked a distributor to impersonate. In
  that state, every /api/artifi/preview call returns 403 FORBIDDEN — the
  endpoint requires a distributor scope via effective_distributor_id() —
  and the Stage silently renders blank or the dashed overlay.

  Without this banner, the SA sees an empty stage and has no signal that
  the cause is "I am not impersonating anyone". The banner says it out
  loud and points at the fix.

  Visibility:
    - SA + no impersonation -> show
    - SA + active impersonation -> hide (previews work)
    - Non-SA (Owner / Builder / etc.) -> hide

  Pair: ProductEditor.vue mounts this above ReviewBanner. Stage.vue
  short-circuits cacheArgs to null in the same state so no 403s fire
  while the banner is visible.
-->
<template>
    <div
        v-if="show"
        data-testid="sa-impersonation-banner"
        class="sa-impersonation-banner"
        role="status"
    >
        <span
            class="material-icons sa-impersonation-banner-icon"
            aria-hidden="true"
        >info</span>
        <span class="sa-impersonation-banner-text">
            Previews are disabled — impersonate a distributor to see live
            garment renders.
        </span>
        <!--
          Finding 5 (2026-07-09): target the working /admin/distributors
          list (route name distributor_list) -- /superadmin/distributors is
          not a registered route and rendered a 404. That list is where the
          SA picks a distributor and starts the Open -> Impersonate flow.
        -->
        <a
            data-testid="sa-impersonation-banner-pick"
            class="sa-impersonation-banner-pick"
            href="/admin/distributors"
        >Pick a distributor</a>
    </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useImpersonationStore } from "@/stores/impersonation";

const store = useStore();
const impersonation = useImpersonationStore();

// Vuex getter: only true when the logged-in account has role 'Super Admin'.
const isSuperAdmin = computed(() => Boolean(store.getters.isSuperAdmin));

// Show when the SA is logged in WITHOUT an active impersonation session.
// Non-SAs never see this banner.
const show = computed(
    () => isSuperAdmin.value && !impersonation.isActive,
);
</script>

<style scoped>
.sa-impersonation-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px;
    background: var(--tf-bg-info-subtle, #eff6ff);
    color: var(--tf-text-info, #1d4ed8);
    border-bottom: 1px solid var(--tf-border-info, #bfdbfe);
    font-size: 13px;
}

.sa-impersonation-banner-icon {
    font-size: 18px;
    line-height: 1;
    color: var(--tf-text-info, #1d4ed8);
}

.sa-impersonation-banner-text {
    flex: 1 1 auto;
}

.sa-impersonation-banner-pick {
    color: var(--tf-text-info, #1d4ed8);
    font-weight: 600;
    text-decoration: underline;
    flex: 0 0 auto;
}
</style>
